import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import Form, { Field, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import { gql } from '../../graphql/generated/gql';
import type { AuthenticationMode } from '../../graphql/generated/graphql';
import { INSTALLABLE_PLATFORM_INFO_MAP } from '../../integrations/IntegrationPlatformInfo';
import useSSOPlatforms from '../../integrations/useSSOPlatforms';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import ExternalServiceCard from '../ExternalServiceCard';
import AdminSSOUserCreationSettingCard from './AdminSSOUserCreationSettingCard';
import * as styles from './AdminSecurityView.module.scss';
import AllowedIframeHostsSettings from './AllowedIframeHostsSettings';
import InvitationSettings from './InvitationSettings';
import PublicContentSettings from './PublicContentSettings';
import TopLevelTopicSettings from './TopLevelTopicSettings';
import UserGroupsSettings from './UserGroupsSettings';

gql(`
  fragment AuthenticationSettings_settings on OrganizationSettings {
    authentication
  }
`);

const UPDATE_AUTHENTICATION_SETTINGS_MUTATION = gql(/** @complexity 4 */ `
  mutation updateAuthenticationSettings($authentication: AuthenticationMode!) {
    organization: updateOrganization(settings: { authentication: $authentication }) {
      id
      settings {
        ...AuthenticationSettings_settings
      }
    }
  }
`);

const ORGANIZATION_SETTING_QUERY = gql(/** @complexity 3 */ `
  query adminSecuritySetting {
    session: currentSession {
      organization {
        id
        settings {
          ...AuthenticationSettings_settings
          ...InvitationSettings_settings
          ...UserGroupsSettings_settings
          ...PublicContentSettings_settings
          ...TopLevelTopicSettings_settings
          ...AllowedIframeHostsSettings_settings
        }
      }
    }
  }
`);

const AUTHENTICATION_LABELS = {
  SSO_AND_GUEST_PASSWORD:
    'Admins and regular users are required to log in using SSO. Guests log in with a password.',
  SSO_AND_PASSWORD: 'Anyone can log in with either SSO or a password.',
  SSO_ONLY: 'All users are required to log in using SSO.',
};

const AdminSecurityView = () => {
  const [isEditingEnforcement, setIsEditingEnforcement] = useState(false);
  const ssoPlatforms = useSSOPlatforms();

  const { data } = useQuery(ORGANIZATION_SETTING_QUERY);

  const [updateAuthenticationSettings] = useMutation(
    UPDATE_AUTHENTICATION_SETTINGS_MUTATION,
  );

  const authenticationSetting =
    data?.session.organization.settings?.authentication;

  return (
    <SettingsContainerView title="Security">
      {!authenticationSetting || !ssoPlatforms ? (
        <LoadingIndicator />
      ) : (
        <>
          <SettingsSection header="SSO providers">
            <Cards>
              {ssoPlatforms.length > 0 ? (
                ssoPlatforms.map((platform) => {
                  const platformInfo = INSTALLABLE_PLATFORM_INFO_MAP[platform];
                  return (
                    !!platformInfo && (
                      <ExternalServiceCard
                        {...platformInfo}
                        key={platform}
                        action={
                          <Button
                            variant="secondary"
                            to={`/admin/integrations/${platform}`}
                          >
                            Manage
                          </Button>
                        }
                      />
                    )
                  );
                })
              ) : (
                <SettingCard
                  actions={
                    <Button to="/admin/integrations" variant="secondary">
                      Manage integrations
                    </Button>
                  }
                  description="You have not set up any SSO providers."
                />
              )}
            </Cards>
          </SettingsSection>
          <SettingsSection header="SSO settings">
            <Cards>
              {isEditingEnforcement ? (
                <SettingCard
                  title="Enforcement"
                  description={
                    <Form<{ authenticationSetting: AuthenticationMode }>
                      initialValues={{ authenticationSetting }}
                      onSubmit={(values) =>
                        updateAuthenticationSettings({
                          variables: {
                            authentication: values.authenticationSetting,
                          },
                        }).then(() => {
                          setIsEditingEnforcement(false);
                        })
                      }
                    >
                      <div className={styles.formFields}>
                        {(
                          [
                            'SSO_ONLY',
                            'SSO_AND_GUEST_PASSWORD',
                            'SSO_AND_PASSWORD',
                          ] as const
                        ).map((value) => (
                          <Field
                            key={value}
                            label={AUTHENTICATION_LABELS[value]}
                            name="authenticationSetting"
                            type="radio"
                            value={value}
                          />
                        ))}
                      </div>
                      <div className={styles.formFooter}>
                        <Submit variant="primary">Save</Submit>
                      </div>
                    </Form>
                  }
                />
              ) : (
                <SettingCard
                  actions={
                    <Button
                      disabled={ssoPlatforms.length === 0}
                      onClick={() => {
                        setIsEditingEnforcement(true);
                      }}
                      variant="secondary"
                    >
                      Edit
                    </Button>
                  }
                  disabled={ssoPlatforms.length === 0}
                  title="Enforcement"
                  description={
                    ssoPlatforms.length > 0
                      ? AUTHENTICATION_LABELS[authenticationSetting]
                      : 'You must have at least one SSO provider to set up enforcement.'
                  }
                />
              )}
              <AdminSSOUserCreationSettingCard
                disabled={ssoPlatforms.length === 0}
              />
            </Cards>
          </SettingsSection>
          <SettingsSection header="Permissions">
            <Cards>
              <InvitationSettings
                settings={data.session.organization.settings}
              />
              <UserGroupsSettings
                settings={data.session.organization.settings}
              />
              <TopLevelTopicSettings
                settings={data.session.organization.settings}
              />
              <PublicContentSettings
                settings={data.session.organization.settings}
              />
              <AllowedIframeHostsSettings
                settings={data.session.organization.settings}
              />
            </Cards>
          </SettingsSection>
        </>
      )}
    </SettingsContainerView>
  );
};

export default AdminSecurityView;
